'use client'

import React, { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import featureData from '@/public/feature.json'

type PlanKey = 'free' | 'pro' | 'advanced' | 'enterprise'

type FeatureValue = string | boolean

type FeatureItem = {
  name: string
  free: FeatureValue
  pro: FeatureValue
  advanced: FeatureValue
  enterprise: FeatureValue
}

const planColumns: Array<{ key: PlanKey; label: string; emphasized?: boolean }> = [
  { key: 'free', label: '免费版' },
  { key: 'pro', label: '标准版' },
  { key: 'advanced', label: '高级版', emphasized: true },
  { key: 'enterprise', label: '企业版' }
]

export function FeatureComparison() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(featureData.features[0]?.category ?? null)
  const { features } = featureData

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8">
      {/* 桌面端直接展示 4 档套餐对比表，和首页 Pricing 保持同一顺序。 */}
      <div className="hidden lg:block">
        <table className="w-full border-collapse overflow-hidden rounded-[24px] bg-white/80 text-[#353745] shadow-[0px_18px_40px_rgba(90,66,235,0.08)]">
          <thead>
            <tr className="h-[90px] border text-lg font-semibold xl:text-xl">
              <th className="w-[160px] border px-4 text-center">权益类别</th>
              <th className="w-[220px] border px-4 text-center">权益明细</th>
              {planColumns.map(plan => (
                <th
                  key={plan.key}
                  className={cn(
                    'w-[180px] border px-4 text-center',
                    plan.emphasized && 'border-[#B5B0FF] bg-[#F3F0FF]',
                    plan.key === 'enterprise' && 'bg-[#FAFBFF]'
                  )}
                >
                  {plan.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((category, categoryIndex) => (
              <React.Fragment key={category.category}>
                {category.details.map((item, itemIndex) => {
                  const detail = item as FeatureItem

                  return (
                    <tr key={`${category.category}-${detail.name}`} className="border text-center text-sm font-normal hover:bg-[#FBFBFE] xl:text-[15px]">
                      {itemIndex === 0 ? (
                        <td className="border px-4 py-5 font-medium" rowSpan={category.details.length}>
                          {category.category}
                        </td>
                      ) : null}

                      <td className="border px-4 py-5 text-center">{detail.name}</td>

                      {planColumns.map(plan => (
                        <td
                          key={`${detail.name}-${plan.key}`}
                          className={cn(
                            'border px-4 py-5 text-center leading-6',
                            plan.emphasized && 'border-[#B5B0FF] bg-[#FCFBFF]',
                            categoryIndex === features.length - 1 && itemIndex === category.details.length - 1 && plan.emphasized && 'border-b-[#B5B0FF]'
                          )}
                        >
                          {formatFeatureValue(detail[plan.key])}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* 移动端继续保留折叠结构，但每一项扩成 4 档，避免横向表格挤爆。 */}
      <div className="space-y-4 lg:hidden">
        {features.map(category => (
          <div key={category.category} className="overflow-hidden rounded-[20px] border border-[#E6E9FA] bg-white/90 shadow-[0px_10px_30px_rgba(90,66,235,0.06)]">
            <button
              type="button"
              className="flex w-full items-center justify-between px-5 py-4 text-left"
              onClick={() => setExpandedCategory(expandedCategory === category.category ? null : category.category)}
            >
              <span className="text-base font-semibold text-[#353745]">{category.category}</span>
              <ChevronDown className={cn('h-5 w-5 text-[#7B8099] transition-transform duration-200', expandedCategory === category.category && 'rotate-180')} />
            </button>

            <AnimatePresence initial={false}>
              {expandedCategory === category.category ? (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t border-[#EEF1FF]">
                  {category.details.map(item => {
                    const detail = item as FeatureItem

                    return (
                      <div key={`${category.category}-${detail.name}`} className="border-b border-[#EEF1FF] px-5 py-4 last:border-b-0">
                        <div className="mb-4 text-sm font-semibold text-[#353745]">{detail.name}</div>
                        <div className="grid grid-cols-2 gap-3">
                          {planColumns.map(plan => (
                            <div
                              key={`${detail.name}-${plan.key}`}
                              className={cn(
                                'rounded-2xl border border-[#EEF1FF] bg-[#FAFBFF] px-3 py-3',
                                plan.emphasized && 'border-[#CFC9FF] bg-[#F6F3FF]'
                              )}
                            >
                              <div className="mb-2 text-xs font-medium text-[#7B8099]">{plan.label}</div>
                              <div className="text-sm leading-6 text-[#353745]">{formatFeatureValue(detail[plan.key])}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  )
}

function formatFeatureValue(content: FeatureValue | undefined) {
  if (typeof content === 'string') {
    return content
  }

  if (content === true) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="mx-auto h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M9.99935 18.3332C12.3005 18.3332 14.3839 17.4004 15.8919 15.8924C17.3999 14.3843 18.3327 12.301 18.3327 9.99984C18.3327 7.69867 17.3999 5.61534 15.8919 4.10728C14.3839 2.59925 12.3005 1.6665 9.99935 1.6665C7.69818 1.6665 5.61485 2.59925 4.10679 4.10728C2.59876 5.61534 1.66602 7.69867 1.66602 9.99984C1.66602 12.301 2.59876 14.3843 4.10679 15.8924C5.61485 17.4004 7.69818 18.3332 9.99935 18.3332Z"
          fill="#5A42EB"
          fillOpacity="0.08"
        />
        <path d="M6.66602 10L9.16602 12.5L14.166 7.5" stroke="#8A8FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  if (content === false) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="mx-auto h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M9.99935 18.3332C14.6017 18.3332 18.3327 14.6022 18.3327 9.99984C18.3327 5.39746 14.6017 1.6665 9.99935 1.6665C5.39698 1.6665 1.66602 5.39746 1.66602 9.99984C1.66602 14.6022 5.39698 18.3332 9.99935 18.3332Z"
          fill="#5A42EB"
          fillOpacity="0.08"
        />
        <path d="M7.08398 12.9168L12.9173 7.0835" stroke="#8A8FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.91732 7.91683L7.08398 7.0835" stroke="#8A8FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.9173 12.9168L12.084 12.0835" stroke="#8A8FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  return '—'
}
